import { useEffect, useState } from 'react';
import { useSession } from './useSession.js';
import { PrerequisitesPanel, RoomsPanel } from './views/SetupView.js';
import { CheckInView } from './views/CheckInView.js';
import { LiveView } from './views/LiveView.js';
import { ReviewView } from './views/ReviewView.js';

const TABS = ['Rooms', 'Check-in', 'Live control', 'Review'] as const;
type Tab = (typeof TABS)[number];

type Summary = { eventId: string; title: string; state: string };

/** Operator console: runs inside the Zoom client for the person running the evening. */
export function OperatorApp() {
  const { session } = useSession();
  const rec = session.engine?.record ?? null;
  const [events, setEvents] = useState<Summary[] | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [held, setHeld] = useState<{ eventId: string; by: string } | null>(null);
  const [tab, setTab] = useState<Tab>('Rooms');

  const refresh = () => void session.listEvents().then(setEvents).catch((e: unknown) => setMsg(String(e)));
  useEffect(() => {
    if (!rec) refresh();
  }, [rec?.eventId, session.identity.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const plain = (e: unknown) => (e instanceof Error ? e.message.replace(/^[A-Z]+ \S+ -> \d+: /, '').replace(/^\{"error":"(.*)"\}$/, '$1') : String(e));

  /** Open an event. If another controller (often the admin page) holds it, offer to take control. */
  const open = async (eventId: string, take = false) => {
    setMsg(null);
    setHeld(null);
    try {
      await session.loadEvent(eventId, take);
      setTab('Rooms');
    } catch (e) {
      const text = plain(e);
      const who = /Control is held by (\S+)/.exec(text)?.[1];
      if (who) setHeld({ eventId, by: who });
      else setMsg(text);
    }
  };

  const join = async () => {
    setMsg(null);
    try {
      const r = await session.client.redeem(code);
      setCode('');
      await open(r.eventId);
    } catch (e) {
      setMsg(plain(e));
    }
  };

  return (
    <div>
      <h1>Rotation Controller</h1>
      <p className="muted">
        {session.adapterKind === 'zoom' ? 'Inside Zoom' : 'Not inside Zoom'} ·{' '}
        {session.context ? `role ${session.context.role}, ${session.context.location} session` : 'no meeting context'}
        {rec ? ` · ${rec.title} · state ${rec.state}` : ''}
      </p>
      {session.notice && <div className="banner banner-info">{session.notice}</div>}
      {session.error && <div className="banner banner-error" role="alert">{session.error}</div>}
      {msg && <div className="banner banner-error" role="alert">{msg}</div>}
      {held && !rec && (
        <div className="banner banner-warn" role="alert">
          <p>This evening is already open in another place ({held.by}), most likely the admin page in a browser. Only one place can control it at a time.</p>
          <button className="primary" onClick={() => void open(held.eventId, true)}>Take control here</button>{' '}
          <button className="secondary" onClick={() => setHeld(null)}>Not now</button>
        </div>
      )}

      {!rec && (
        <div>
          <h2>Join an evening</h2>
          <p className="muted">Enter the code your organiser sent you. You only need to do this once for each evening.</p>
          <div className="row">
            <label htmlFor="code">Join code</label>
            <input id="code" type="text" autoComplete="off" spellCheck={false} placeholder="ABCDE-23456" value={code} onChange={(e) => setCode(e.target.value)} />
            <button className="primary" disabled={code.replace(/[^A-Za-z0-9]/g, '').length !== 10} onClick={() => void join()}>
              Join
            </button>
          </div>
          {events && events.length > 0 && (
            <div>
              <h2>Your evenings</h2>
              <ul>
                {events.map((e) => (
                  <li key={e.eventId}>
                    <button className="secondary" onClick={() => void open(e.eventId)}>
                      {e.title} ({e.state})
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {events && events.length === 0 && <p className="muted">You have not joined an evening yet.</p>}
        </div>
      )}

      {rec && (
        <div>
          {session.mode === 'backend' && !session.hasControl && (
            <div className="banner banner-warn">
              Another controller holds control of this event{session.leaseHolder ? ` (${session.leaseHolder})` : ''}.{' '}
              <button className="secondary" onClick={() => void session.takeControl()}>Take control</button>
            </div>
          )}
          <p>
            <button className="secondary" onClick={() => void session.closeEvent()}>← Your evenings</button>
          </p>
          <nav className="tabs" role="tablist" aria-label="Controller views">
            {TABS.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>{t}</button>
            ))}
          </nav>
          {tab === 'Rooms' && (
            <>
              <PrerequisitesPanel session={session} />
              <RoomsPanel session={session} />
            </>
          )}
          {tab === 'Check-in' && <CheckInView session={session} />}
          {tab === 'Live control' && <LiveView session={session} />}
          {tab === 'Review' && <ReviewView session={session} />}
        </div>
      )}
    </div>
  );
}
